import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  Image,
  Text,
  StyleSheet,
  Animated,
  ActivityIndicator,
  Platform,
  TouchableWithoutFeedback,
} from 'react-native';

interface ImageRendererProps {
  fileUrl?: string;
  thumbnailUrl?: string;
  isLoading: boolean;
  hasError: boolean;
  fileName: string;
}

export const ImageRenderer: React.FC<ImageRendererProps> = ({
  fileUrl,
  thumbnailUrl,
  isLoading,
  hasError,
  fileName,
}) => {
  const scale = useRef(new Animated.Value(1)).current;
  const [currentScale, setCurrentScale] = useState(1);
  const lastScale = useRef(1);
  const lastTapRef = useRef(0);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      const next = currentScale > 1.1 ? 1 : 2.5;
      setCurrentScale(next);
      lastScale.current = next;
      Animated.spring(scale, { toValue: next, useNativeDriver: true }).start();
    }
    lastTapRef.current = now;
  }, [currentScale, scale]);

  if (hasError) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>Failed to load image</Text>
        <Text style={styles.errorSub}>{fileName}</Text>
      </View>
    );
  }

  const displayUri = fileUrl ?? thumbnailUrl;

  return (
    <View style={styles.container}>
      {displayUri ? (
        <TouchableWithoutFeedback onPress={handleDoubleTap} accessibilityLabel={fileName}>
          <Animated.View style={[styles.imageWrap, { transform: [{ scale }] }]}>
            <Image
              source={{ uri: displayUri }}
              style={styles.image}
              resizeMode="contain"
            />
          </Animated.View>
        </TouchableWithoutFeedback>
      ) : null}
      {isLoading && (
        <View style={styles.loadingOverlay}>
          {thumbnailUrl ? (
            <Image
              source={{ uri: thumbnailUrl }}
              style={[styles.image, styles.blurredThumb]}
              resizeMode="contain"
            />
          ) : null}
          <ActivityIndicator size="large" color="#fff" style={styles.spinner} />
        </View>
      )}
      {!displayUri && !isLoading && !hasError && (
        <View style={styles.center}>
          <ActivityIndicator size="large" color="#fff" />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageWrap: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
    ...Platform.select({
      web: { maxHeight: '80vh' } as unknown as object,
      default: {},
    }),
  },
  blurredThumb: {
    opacity: 0.4,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
  spinner: {
    position: 'absolute',
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 4,
  },
  errorSub: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
  },
});
